import {
	type InvestmentImportResponse,
	type TemplateImportType,
	type UniverseConversionNamedItems,
	type UniverseImportConverterType,
	type UniverseImport as UniverseImportResponse,
	type UniverseNamedConversion,
	IntegrationsControllerV3ApiFactory,
	IntegrationUniverseControllerApiFactory,
} from "@eu.mdotm.api/sphere";
import { reportPlatformError } from "$root/api/error-reporting";
import { useApiGen } from "$root/api/hooks";
import BaseDropzoneBlock, { type Converted, type DropzoneState } from "$root/components/Upload/BaseDropzoneBlock";
import { platformToast } from "$root/notification-system/toast";
import { axiosExtract } from "$root/third-party-integrations/axios";
import { trackMixPanelEvent } from "$root/third-party-integrations/initMixPanel";
import { capitalizeString, downloadContentDisposition, parallelize, parallelizeWithAllSettled } from "$root/utils";
import {
	type DialogProps,
	type Option,
	type TableColumn,
	ActionText,
	AutoSortTable,
	Banner,
	Collapsible,
	DefaultCollapsibleHeader,
	Dialog,
	DialogFooter,
	Row,
	Text,
} from "@mdotm/shadcn/legacy/components";
import { Button, Checkbox, FormField, type FormProps, Select, SubmitButton } from "@mdotm/shadcn/mdotm/components";
import {
	type SpawnResult,
	adaptAnimatedNodeProvider,
	generateUniqueDOMId,
	spawn,
} from "@mdotm/shadcn/legacy/react-extensions";
import { ForEach, useUnsafeUpdatedRef } from "@mdotm/utils/react-extra";
import { themeCSSVars } from "@mdotm/shadcn/legacy/themes";
import { unpromisify } from "@mdotm/shadcn/legacy/utils";
import { Set } from "immutable";
import { useCallback, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import XLSX from "xlsx";
import { universeRepository } from "./repository";

type Id<T> = T & { id: string };
const concurrency = 32;
function ConversionBlock({
	converter,
	status,
	converted,
	onSubmit,
	onChangeStatus,
}: {
	status: DropzoneState;
	onChangeStatus(status: DropzoneState): void;
	converter: UniverseImportConverterType;
	onSubmit(conversions: Array<Converted<UniverseConversionNamedItems>>): void;
	converted: Array<Converted<UniverseConversionNamedItems>>;
}) {
	const { t } = useTranslation();
	const integrationsApi = useApiGen(IntegrationUniverseControllerApiFactory);

	const reset = useCallback(() => {
		onChangeStatus("empty");
		onSubmit([]);
	}, [onChangeStatus, onSubmit]);

	const onChange = useCallback(
		async (files: File[], type: UniverseImportConverterType) => {
			try {
				reset();
				onChangeStatus("loading");
				const filesToConverter = files.map(
					(file) => () => axiosExtract(integrationsApi.convertUniverseCheckFrom(type, file)),
				);
				const responseStack = await parallelizeWithAllSettled(filesToConverter, { concurrency });
				const assocFileWithConversion = files.map((file, i) => ({
					filename: file.name,
					conversion: responseStack[i].status === "fulfilled" ? responseStack[i].value : undefined,
				}));

				const succededResponses = assocFileWithConversion.filter(
					({ conversion }) => conversion !== undefined,
				) as Converted<UniverseConversionNamedItems>[];

				const failedResponses = assocFileWithConversion.filter(
					({ conversion }) => conversion === undefined,
				) as Converted<undefined>[];

				if (failedResponses.length > 0) {
					failedResponses.forEach((x) => {
						platformToast({
							children: `Failed to convert ${x.filename ?? "-"}`,
							severity: "error",
							icon: "Portfolio",
						});
					});
				}

				if (succededResponses.length > 0) {
					onSubmit(succededResponses);
				}

				if (failedResponses.length === succededResponses.length) {
					onChangeStatus("error");
				} else {
					onChangeStatus("success");
				}
			} catch (error) {
				onChangeStatus("error");

				throw error;
			}
		},
		[integrationsApi, onChangeStatus, onSubmit, reset],
	);

	const failedConversion = useMemo(() => converted.filter(({ conversion }) => conversion === undefined), [converted]);

	return (
		<>
			<BaseDropzoneBlock
				converted={converted}
				disabled={status === "loading"}
				onDelete={reset}
				status={status}
				onChange={(x) => {
					if (x === null) {
						return reset();
					}

					unpromisify(() => onChange(x, converter))();
				}}
			/>
			{status === "loading" && (
				<div className="my-4">
					<Banner severity="info">{t("PORTFOLIOS.UPLOAD_DURATION_LIMIT")}</Banner>
				</div>
			)}
			{failedConversion.length > 0 && (
				<div className="mb-4">
					<Banner severity="warning" title="Failed to convert these files">
						{failedConversion.map(({ filename }) => filename).join(", ")}
					</Banner>
				</div>
			)}
		</>
	);
}

function OverrideTable({
	selected,
	onChange,
	rows,
}: {
	rows: Array<Id<UniverseNamedConversion>>;
	onChange(value: Set<string>): void;
	selected: Set<string>;
}) {
	const onChangeRef = useUnsafeUpdatedRef(onChange);
	const rowsId = useMemo(() => rows.map(({ id }) => id), [rows]);
	const columns = useMemo<TableColumn<Id<UniverseNamedConversion>>[]>(
		() => [
			{
				name: "name",
				header: "",
				content: (row) => row.universeImport?.name ?? "-",
				minWidth: 200,
			},
			{
				name: "overwrite",
				header: (headerProps) => (
					<Row {...headerProps} gap={8} alignItems="center">
						<Checkbox
							checked={
								selected.size === 0
									? true
									: selected.size === rowsId.length
										? false
										: selected.size > 0
											? "indeterminate"
											: selected.size === 0
							}
							onChange={() => onChangeRef.current(Set())}
						>
							<Text type="Body/S/BOLD-UPPERCASE" color={themeCSSVars.palette_N500} className="truncate">
								Overwrite
							</Text>
						</Checkbox>
					</Row>
				),
				content: ({ id }, cellProps) => (
					<Row {...cellProps} alignItems="center">
						<Checkbox checked={!selected.has(id)} onChange={() => onChangeRef.current(selected.remove(id))} />
					</Row>
				),
				width: 120,
			},
			{
				name: "ignore",
				header: (headerProps) => (
					<Row {...headerProps} gap={8} alignItems="center">
						<Checkbox
							checked={
								selected.size > 0 && selected.size < rowsId.length ? "indeterminate" : selected.size === rowsId.length
							}
							onChange={() => onChangeRef.current(Set(rowsId))}
						>
							<Text type="Body/S/BOLD-UPPERCASE" color={themeCSSVars.palette_N500} className="truncate">
								Ignore
							</Text>
						</Checkbox>
					</Row>
				),
				content: ({ id }, cellProps) => (
					<Row {...cellProps} alignItems="center">
						<Checkbox
							checked={selected.has(id)}
							onChange={() => {
								onChangeRef.current(selected.add(id));
								console.log("ignore");
							}}
						/>
					</Row>
				),
				width: 120,
			},
		],
		[onChangeRef, rowsId, selected],
	);

	return (
		<AutoSortTable
			rows={rows}
			columns={columns}
			className="bg-transparent! max-h-[200px]"
			rowClassName="bg-transparent! border-[#A0A7B6]!"
		/>
	);
}

const converterToTemplateType = {
	BLOOMBERG_CONVERTER: "BLOOMBERG_UNIVERSE_IMPORT",
	EASIM_CONVERTER: "EASIM_UNIVERSE_IMPORT",
	SPHERE_TEMPLATE_CONVERTER: "SPHERE_UNIVERSE_IMPORT",
	SPHERE_BULK_TEMPLATE_CONVERTER: "SPHERE_BULK_UNIVERSE_IMPORT",
} satisfies { [key in UniverseImportConverterType]: TemplateImportType };

type InvestmentDialogProps = {
	converters: Array<UniverseImportConverterType>;
	onImportFinished(params: {
		imported: Array<InvestmentImportResponse>;
		failed: Array<{ universe: UniverseImportResponse; err: unknown }>;
	}): void;
	qualifier?: string;
	show: boolean;
	onClose(): void;
	onAnimationStateChange?: DialogProps["onAnimationStateChange"];
};

function UniverseImport({
	converters,
	onImportFinished,
	show,
	onClose,
	onAnimationStateChange,
}: InvestmentDialogProps): React.JSX.Element {
	const { t } = useTranslation();
	const options = useMemo(
		() =>
			converters.map(
				(converter): Option<UniverseImportConverterType> => ({
					label: capitalizeString(t(`IMPORT.${converter}`).toLowerCase()),
					value: converter,
				}),
			),
		[converters, t],
	);

	const [selectedConverter, setSelectedConverter] = useState(converters[0]);
	const [converted, setConverted] = useState<Array<Converted<UniverseConversionNamedItems>>>([]);
	const [dropzoneStatus, setDropzoneStatus] = useState<DropzoneState>("empty");

	const [universeToIgnore, _setUniverseToIgnore] = useState(Set<string>());
	const latestInvestmentToIgnore = useRef(universeToIgnore);
	function setInvestmentToIgnore(universes: Set<string>) {
		_setUniverseToIgnore(universes);
		latestInvestmentToIgnore.current = universes;
	}

	const flatConversion = useMemo(
		() => converted.flatMap((x) => x.conversion.conversions ?? []).map((x) => ({ ...x, id: generateUniqueDOMId() })),
		[converted],
	);

	const conversionWithErrors = useMemo(
		() => converted.filter((x) => x.conversion.errors && x.conversion.errors.length > 0),
		[converted],
	);

	const existingInvestments = useMemo(() => {
		return flatConversion.filter(({ exist }) => exist);
	}, [flatConversion]);

	const disabled = useMemo(
		() => conversionWithErrors.length > 0 || converted.length === 0,
		[conversionWithErrors.length, converted.length],
	);

	const integrationsApiV2 = useApiGen(IntegrationUniverseControllerApiFactory);

	const onSubmitAsync = useCallback<FormProps<void>["onSubmitAsync"]>(
		async (_, { progressCb }) => {
			const fileToSubmit = flatConversion.flatMap((universe) => {
				if (universeToIgnore.has(universe.id) || !universe.universeImport) {
					return [];
				}

				return [universe.universeImport];
			});

			if (fileToSubmit.length === 0) {
				platformToast({
					children: "No universe imported",
					severity: "info",
					icon: "Dowload",
				});
				onImportFinished({ failed: [], imported: [] });
				return;
			}

			let progress = 0;
			const failed: { universe: UniverseImportResponse; err: unknown }[] = [];
			const imported: InvestmentImportResponse[] = [];
			const callStack = fileToSubmit.map(
				(universe) => () =>
					integrationsApiV2
						.importUniverseAsync(universe)
						.then((resp) => imported.push(resp.data))
						.catch((err) => failed.push({ universe, err }))
						.finally(() => {
							progress++;
							progressCb(progress / callStack.length);
						}),
			);
			await parallelize(callStack, { concurrency });

			universeRepository.invalidateLists();

			trackMixPanelEvent("universe.import", {
				failed: failed.map((f) => (f.err ? String(f.err) : "unknown")),
				imported: imported.length,
				format: selectedConverter,
			});

			if (failed.length === 0) {
				platformToast({
					children: "Sphere has taken over your request",
					severity: "info",
					icon: "Dowload",
				});
			} else if (failed.length === callStack.length) {
				platformToast({
					children: "No universe imported",
					severity: "error",
					icon: "Dowload",
				});
			} else {
				platformToast({
					children: `Imported ${callStack.length - failed.length} out of ${callStack.length} universes`,
					severity: "warning",
					icon: "Dowload",
				});
			}

			onImportFinished({ imported, failed });
		},
		[flatConversion, integrationsApiV2, onImportFinished, selectedConverter, universeToIgnore],
	);

	const handleDownloadErrorReport = useCallback((report: Array<Converted<UniverseConversionNamedItems>>) => {
		const xlsSheet = report.flatMap(
			({ conversion, filename }) =>
				conversion.errors?.map(({ errorMessage, investmentName }) => ({
					File: filename,
					Portfolio: investmentName,
					Message: errorMessage,
				})) ?? [],
		);

		const reportWS = XLSX.utils.json_to_sheet(xlsSheet);
		const wb = XLSX.utils.book_new();
		XLSX.utils.book_append_sheet(wb, reportWS, "Report Errors");
		XLSX.writeFile(wb, "Errors file.xlsx");
	}, []);

	const integrationsApi = useApiGen(IntegrationsControllerV3ApiFactory);
	const handleDownloadTemplate = useCallback(
		(convertTo: UniverseImportConverterType) =>
			integrationsApi
				.getUploadTemplate(converterToTemplateType[convertTo], {
					responseType: "blob",
				})
				.then(downloadContentDisposition)
				.catch((e) => {
					platformToast({
						children: "Oops, Something went wrong, please re-try",
						severity: "error",
						icon: "Portfolio",
					});
					reportPlatformError(e, "ERROR", "universe", { message: "unable to download template" });
					throw e;
				}),
		[integrationsApi],
	);

	return (
		<Dialog
			size="large"
			show={show}
			onClose={onClose}
			onSubmitAsync={onSubmitAsync}
			onAnimationStateChange={onAnimationStateChange}
			header="Import file"
			footer={
				<DialogFooter
					primaryAction={
						<SubmitButton palette="primary" disabled={disabled}>
							{t("BUTTON.SAVE")}
						</SubmitButton>
					}
					neutralAction={
						<Button palette="tertiary" onClick={onClose}>
							{t("BUTTON.CANCEL")}
						</Button>
					}
				/>
			}
		>
			<div className="mb-4">
				<FormField label="File format">
					{(forward) => (
						<Select
							{...forward}
							options={options}
							value={selectedConverter}
							onChange={(converter) => {
								setConverted([]);
								setDropzoneStatus("empty");
								if (converter) {
									setSelectedConverter(converter);
								}
							}}
							className="w-[300px]"
						/>
					)}
				</FormField>
			</div>
			<ol className="list-decimal ml-4 mb-4">
				<li>
					Download the <ActionText onClickAsync={() => handleDownloadTemplate(selectedConverter)}>template</ActionText>
				</li>
				<li>Insert your universe composition</li>
				<li>Upload the edited template. Only files formatted like the template will be accepted.</li>
			</ol>
			<ConversionBlock
				converted={converted}
				onSubmit={setConverted}
				status={dropzoneStatus}
				onChangeStatus={setDropzoneStatus}
				converter={selectedConverter}
			/>

			{existingInvestments.length > 0 && (
				<div className="mb-4 space-y-4">
					<Banner severity="warning" title="Update existing universes">
						<p className="mb-2">Some universes already exist. Would you like to overwrite them?</p>
					</Banner>
					<OverrideTable rows={existingInvestments} selected={universeToIgnore} onChange={setInvestmentToIgnore} />
				</div>
			)}

			{!disabled && dropzoneStatus === "success" && (
				<div className="mb-4">
					<Banner severity="success" title="File uploaded correctly">
						Your universes have been converted by Sphere. Click &quot;
						<strong>Save</strong>&quot; to start analyzing and processing your instruments.
					</Banner>
				</div>
			)}

			{conversionWithErrors.length > 0 && (
				<Banner severity="error" title="Please review the template format and upload the file again" className="mb-4">
					<p>The uploaded files have some formatting errors</p>
					{"\n"}
					<ActionText as="button" onClick={() => handleDownloadErrorReport(conversionWithErrors)}>
						Download errors file
					</ActionText>
				</Banner>
			)}

			<ForEach collection={conversionWithErrors}>
				{(item) => (
					<Collapsible
						header={(params) => (
							<DefaultCollapsibleHeader
								{...params}
								className={{
									[`bg-transparent! h-[40px] flex items-center`]: true,
								}}
							>
								{item.filename}
							</DefaultCollapsibleHeader>
						)}
					>
						<ForEach collection={item.conversion.errors ?? []}>
							{(e) => (
								<div className="h-[40px] border-b border-[#eeeef1] transition-[background] data-forceHover:bg-[#EFF0F3] hover:bg-[#EFF0F3] flex space-x-2 items-center px-2">
									<div className="w-14">
										<p className="pr-1 truncate">{e.investmentName}</p>
									</div>
									<p className="pr-1 truncate">{e.errorMessage}</p>
								</div>
							)}
						</ForEach>
					</Collapsible>
				)}
			</ForEach>
		</Dialog>
	);
}

type SpawnUniverseImportDialogParams = Omit<InvestmentDialogProps, "onClose" | "show">;
export function spawnUniverseImportDialog(params: SpawnUniverseImportDialogParams): SpawnResult<void> {
	return spawn<void>(
		adaptAnimatedNodeProvider(({ resolve, show, onHidden }) => (
			<UniverseImport
				{...params}
				show={show}
				onAnimationStateChange={(state) => state === "hidden" && onHidden()}
				onClose={() => resolve()}
				onImportFinished={(...forward) => {
					params.onImportFinished(...forward);
					resolve();
				}}
			/>
		)),
	);
}
export default UniverseImport;
